import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLoja } from './LojaContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  QrCode,
  CheckCircle,
  Copy,
  Loader2,
  CreditCard,
  Landmark,
  RefreshCw,
} from 'lucide-react';

export default function LojaCheckout() {
  const {
    API,
    cart,
    cartTotal,
    clearCart,
    authHeaders,
  } = useLoja();

  const navigate = useNavigate();

  const [step, setStep] = useState('review');
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);

  const [methods, setMethods] = useState([]);
  const [manualPixKeys, setManualPixKeys] = useState([]);

  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedPixKey, setSelectedPixKey] = useState('');

  // Respostas das informações solicitadas em cada produto.
  // Estrutura: { product_id: { field_id: value } }
  const [customFieldValues, setCustomFieldValues] =
    useState({});

  const [loading, setLoading] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    loadPaymentMethods();
    // O carregamento deve ocorrer apenas ao abrir o checkout.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      step !== 'pix' ||
      !order ||
      payment?.provider !== 'mercadopago'
    ) {
      return undefined;
    }

    const timer = setInterval(() => {
      checkPaymentStatus(false);
    }, 5000);

    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [step, order?.id, payment?.provider]);

  const loadPaymentMethods = async () => {
    setLoadingMethods(true);

    try {
      const response = await axios.get(
        `${API}/payment-methods`
      );

      const available = response.data?.methods || [];
      const keys = response.data?.manual_pix_keys || [];

      const methodIds = available
        .map(method =>
          typeof method === 'string'
            ? method
            : method?.id
        )
        .filter(Boolean);

      setMethods(methodIds);
      setManualPixKeys(keys);

      if (methodIds.length === 1) {
        setSelectedMethod(methodIds[0]);
      }

      if (keys.length === 1) {
        setSelectedPixKey(keys[0].id);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        'Não foi possível carregar os meios de pagamento.'
      );
    } finally {
      setLoadingMethods(false);
    }
  };

  const updateCustomFieldValue = (
    productId,
    fieldId,
    value
  ) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [fieldId]: value,
      },
    }));
  };

  const validateCustomFields = () => {
    for (const item of cart) {
      const fields =
        item.metadata?.custom_fields || [];

      for (const field of fields) {
        if (!field.required) continue;

        const value =
          customFieldValues[item.product_id]?.[
            field.id
          ];

        if (
          value === undefined ||
          value === null ||
          String(value).trim() === ''
        ) {
          toast.error(
            `Preencha "${field.label}" em ${item.name}.`
          );

          return false;
        }
      }
    }

    return true;
  };

  const createOrder = async () => {
    const items = cart.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      custom_fields:
        customFieldValues[item.product_id] || {},
    }));

    const response = await axios.post(
      `${API}/orders`,
      { items },
      { headers: authHeaders() }
    );

    return response.data;
  };

  const submitOrder = async () => {
    if (!validateCustomFields()) {
      return;
    }

    if (!selectedMethod) {
      toast.error('Escolha uma forma de pagamento.');
      return;
    }

    if (
      selectedMethod === 'manual_pix' &&
      manualPixKeys.length > 1 &&
      !selectedPixKey
    ) {
      toast.error('Escolha a chave PIX.');
      return;
    }

    setLoading(true);

    try {
      const newOrder = await createOrder();
      setOrder(newOrder);

      if (selectedMethod === 'manual_pix') {
        const keyId =
          selectedPixKey ||
          manualPixKeys[0]?.id ||
          '';

        const response = await axios.post(
          `${API}/payments/manual-pix/${newOrder.id}`,
          {},
          {
            headers: authHeaders(),
            params: keyId
              ? { key_id: keyId }
              : {},
          }
        );

        setPayment({
          ...response.data,
          provider: 'manual',
        });

        setStep('pix');
        return;
      }

      if (selectedMethod === 'mercadopago_pix') {
        const response = await axios.post(
          `${API}/payments/mercadopago-pix/${newOrder.id}`,
          {},
          {
            headers: authHeaders(),
          }
        );

        setPayment({
          ...response.data,
          provider: 'mercadopago',
        });

        setStep('pix');
        return;
      }

      if (selectedMethod === 'mercadopago_card') {
        const response = await axios.post(
          `${API}/payments/mercadopago-card/${newOrder.id}`,
          {},
          {
            headers: authHeaders(),
          }
        );

        const checkoutUrl =
          response.data?.checkout_url;

        if (!checkoutUrl) {
          throw new Error(
            'Mercado Pago não retornou o endereço do checkout.'
          );
        }

        // O pedido já foi criado. Limpamos o carrinho antes
        // de enviar o cliente ao checkout hospedado.
        clearCart();

        window.location.href = checkoutUrl;
        return;
      }

      throw new Error(
        'Forma de pagamento inválida.'
      );
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
        err.message ||
        'Erro ao criar pedido.'
      );
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (showToast = true) => {
    if (!order?.id || checking) return;

    setChecking(true);

    try {
      const response = await axios.get(
        `${API}/payments/${order.id}/status`,
        {
          headers: authHeaders(),
        }
      );

      const orderStatus =
        response.data?.order_status ||
        response.data?.status;

      const paymentStatus =
        response.data?.payment?.status ||
        response.data?.payment_status;

      if (
        orderStatus === 'paid' ||
        orderStatus === 'delivered' ||
        paymentStatus === 'approved'
      ) {
        clearCart();
        setStep('done');
        toast.success('Pagamento aprovado!');
        return;
      }

      if (showToast) {
        toast.info(
          payment?.provider === 'manual'
            ? 'Pagamento aguardando confirmação.'
            : 'Pagamento ainda não foi aprovado.'
        );
      }
    } catch (err) {
      if (showToast) {
        toast.error(
          err.response?.data?.detail ||
          'Não foi possível consultar o pagamento.'
        );
      }
    } finally {
      setChecking(false);
    }
  };

  const copyPix = async () => {
    const code = payment?.pix_code;

    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
      toast.success('Código PIX copiado.');
    } catch {
      toast.error('Não foi possível copiar o código.');
    }
  };

  const formatMoney = value =>
    Number(value || 0).toLocaleString(
      'pt-BR',
      {
        style: 'currency',
        currency: 'BRL',
      }
    );

  if (
    cart.length === 0 &&
    step === 'review'
  ) {
    return (
      <div className="text-center py-16 text-slate-400">
        Carrinho vazio
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="max-w-md mx-auto text-center py-12">
        <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />

        <h1 className="text-2xl font-black mb-2">
          Pagamento confirmado!
        </h1>

        <p className="text-slate-400 mb-6">
          Seu pedido foi registrado com sucesso.
        </p>

        <Button
          onClick={() => navigate('/loja/orders')}
          className="bg-fuchsia-500 hover:bg-fuchsia-600"
        >
          Ver meus pedidos
        </Button>
      </div>
    );
  }

  if (step === 'pix' && payment) {
    const qrBase64 =
      payment.qr_code_base64 ||
      payment.qr_code_base64_image;

    return (
      <div className="max-w-md mx-auto">
        <Card className="bg-slate-900 border-slate-800 p-6">

          <div className="text-center mb-5">
            <QrCode className="w-12 h-12 text-fuchsia-400 mx-auto mb-2" />

            <h1 className="text-xl font-bold">
              Pague com PIX
            </h1>

            <p className="text-3xl font-black text-fuchsia-400 mt-2">
              {formatMoney(
                payment.amount || order?.total
              )}
            </p>
          </div>

          {qrBase64 && (
            <div className="bg-white rounded-xl p-3 max-w-[260px] mx-auto mb-5">
              <img
                src={`data:image/png;base64,${qrBase64}`}
                alt="QR Code PIX"
                className="w-full"
              />
            </div>
          )}

          {payment.pix_holder && (
            <div className="bg-slate-800 rounded-lg p-3 mb-3 text-sm">
              <div className="text-xs text-slate-400">
                Titular
              </div>

              <div className="font-medium">
                {payment.pix_holder}
              </div>
            </div>
          )}

          {payment.pix_code && (
            <>
              <div className="bg-slate-800 rounded-lg p-3 mb-4">
                <div className="text-xs text-slate-400 mb-1">
                  PIX Copia e Cola
                </div>

                <div className="text-xs font-mono break-all text-slate-200">
                  {payment.pix_code}
                </div>
              </div>

              <Button
                onClick={copyPix}
                variant="outline"
                className="w-full mb-3 border-slate-700"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar código PIX
              </Button>
            </>
          )}

          <Button
            onClick={() => checkPaymentStatus(true)}
            disabled={checking}
            className="w-full bg-fuchsia-500 hover:bg-fuchsia-600"
          >
            {checking ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Verificar pagamento
              </>
            )}
          </Button>

          <p className="text-xs text-slate-500 mt-4 text-center">
            {payment.provider === 'mercadopago'
              ? 'A confirmação é automática após a aprovação pelo Mercado Pago.'
              : 'No PIX Direto, o pagamento ficará aguardando confirmação da loja.'}
          </p>

        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">

      <h1 className="text-2xl font-bold mb-4">
        Revisar pedido
      </h1>

      <Card className="bg-slate-900 border-slate-800 p-5 mb-4">

        {cart.map(item => {
          const fields =
            item.metadata?.custom_fields || [];

          return (
            <div
              key={item.product_id}
              className="py-3 border-b border-slate-800 last:border-0"
            >
              <div className="flex justify-between gap-3">
                <span>
                  {item.name}{' '}
                  <span className="text-slate-500">
                    x{item.quantity}
                  </span>
                </span>

                <span className="font-medium whitespace-nowrap">
                  {formatMoney(
                    item.price * item.quantity
                  )}
                </span>
              </div>

              {fields.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-xs font-semibold text-fuchsia-300">
                    Informações para este produto
                  </p>

                  {fields.map(field => {
                    const value =
                      customFieldValues[
                        item.product_id
                      ]?.[field.id] || '';

                    const commonProps = {
                      value,
                      required: !!field.required,
                      onChange: event =>
                        updateCustomFieldValue(
                          item.product_id,
                          field.id,
                          event.target.value
                        ),
                    };

                    return (
                      <label
                        key={field.id}
                        className="block"
                      >
                        <span className="block text-sm mb-1.5 text-slate-300">
                          {field.label}

                          {field.required && (
                            <span className="text-red-400 ml-1">
                              *
                            </span>
                          )}
                        </span>

                        {field.type === 'textarea' ? (
                          <textarea
                            {...commonProps}
                            rows="3"
                            placeholder={`Informe ${field.label}`}
                            className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
                          />
                        ) : (
                          <input
                            {...commonProps}
                            type={
                              field.type === 'email'
                                ? 'email'
                                : field.type ===
                                  'password'
                                ? 'password'
                                : field.type === 'number'
                                ? 'number'
                                : field.type === 'url'
                                ? 'url'
                                : 'text'
                            }
                            placeholder={`Informe ${field.label}`}
                            className="w-full h-10 rounded-md border border-slate-700 bg-slate-950 px-3 text-sm"
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        <div className="flex justify-between font-bold text-lg pt-3 mt-2 border-t border-slate-800">
          <span>Total</span>

          <span className="text-fuchsia-400">
            {formatMoney(cartTotal)}
          </span>
        </div>

      </Card>

      <Card className="bg-slate-900 border-slate-800 p-5 mb-4">

        <h2 className="font-bold mb-4">
          Forma de pagamento
        </h2>

        {loadingMethods ? (
          <div className="py-6 text-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Carregando...
          </div>
        ) : methods.length === 0 ? (
          <div className="text-sm text-amber-300 bg-amber-950/30 border border-amber-900 rounded-lg p-3">
            Nenhuma forma de pagamento está disponível no momento.
          </div>
        ) : (
          <div className="space-y-3">

            {methods.includes('manual_pix') && (
              <PaymentChoice
                active={selectedMethod === 'manual_pix'}
                onClick={() =>
                  setSelectedMethod('manual_pix')
                }
                icon={<Landmark className="w-5 h-5" />}
                title="PIX Direto"
                description="Pagamento diretamente para a loja."
              />
            )}

            {methods.includes('mercadopago_pix') && (
              <PaymentChoice
                active={
                  selectedMethod ===
                  'mercadopago_pix'
                }
                onClick={() =>
                  setSelectedMethod(
                    'mercadopago_pix'
                  )
                }
                icon={<QrCode className="w-5 h-5" />}
                title="PIX Mercado Pago"
                description="QR Code com confirmação automática."
              />
            )}

            {methods.includes('mercadopago_card') && (
              <PaymentChoice
                active={
                  selectedMethod ===
                  'mercadopago_card'
                }
                onClick={() =>
                  setSelectedMethod(
                    'mercadopago_card'
                  )
                }
                icon={<CreditCard className="w-5 h-5" />}
                title="Cartão de crédito"
                description="Pagamento seguro pelo Mercado Pago."
              />
            )}

          </div>
        )}

        {selectedMethod === 'manual_pix' &&
          manualPixKeys.length > 1 && (
            <div className="mt-5">
              <label className="block text-xs text-slate-400 mb-2">
                Escolha a chave PIX
              </label>

              <select
                value={selectedPixKey}
                onChange={e =>
                  setSelectedPixKey(
                    e.target.value
                  )
                }
                className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm"
              >
                <option value="">
                  Selecione
                </option>

                {manualPixKeys.map(key => (
                  <option
                    key={key.id}
                    value={key.id}
                  >
                    {key.label} — {key.key_type}
                  </option>
                ))}
              </select>
            </div>
          )}

      </Card>

      <Button
        onClick={submitOrder}
        disabled={
          loading ||
          loadingMethods ||
          methods.length === 0
        }
        className="w-full bg-fuchsia-500 hover:bg-fuchsia-600"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Processando...
          </>
        ) : selectedMethod === 'mercadopago_card' ? (
          'Ir para pagamento'
        ) : selectedMethod ? (
          'Gerar pagamento'
        ) : (
          'Escolha uma forma de pagamento'
        )}
      </Button>

    </div>
  );
}

function PaymentChoice({
  active,
  onClick,
  icon,
  title,
  description,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left border rounded-xl p-4 flex items-center gap-3 transition-colors ${
        active
          ? 'border-fuchsia-500 bg-fuchsia-500/10'
          : 'border-slate-700 hover:border-slate-600'
      }`}
    >
      <div
        className={
          active
            ? 'text-fuchsia-400'
            : 'text-slate-400'
        }
      >
        {icon}
      </div>

      <div>
        <div className="font-semibold text-sm">
          {title}
        </div>

        <div className="text-xs text-slate-500 mt-0.5">
          {description}
        </div>
      </div>
    </button>
  );
}
